import { ARENA, CAMERA_STYLE, TICK_HZ, TILE, createInitialState, opponentOf, type EndMessage, type Player, type PlayerId, type StartMessage, type State } from '@chessshooter/shared';
import { fitWorld, followWorld, screenToWorld, type Camera } from '../camera';
import { BoardController } from '../chess/boardController';
import { resultDetail } from '../chess/status';
import { ChessAudio, ChessSoundEvents } from '../chess/sounds';
import { ArenaAudio } from '../arenaSounds';
import { createHud } from '../hud';
import { attachInput } from '../input';
import { createLobby, lobbyView } from '../lobby';
import type { Connect, TokenStore } from '../net';
import { composeHudState, composeScene, Correction, Interpolation, Prediction } from '../netcode';
import { PING_INTERVAL_MS, STALL_WARNING_MS } from '../netConstants';
import { createRenderer } from '../render';
import { Session, type SessionStatus } from '../session';
import { loadSettings, saveSettings } from '../settings';
import { BOARD_STYLE, MAX_FRAME_MS } from '../uiConstants';
import type { ModeHandle } from './mode';

export interface OnlineModeOptions {
  canvas: HTMLCanvasElement;
  hud: HTMLElement;
  url: string;
  connect: Connect;
  tokens: TokenStore;
}

const TICK_MS = 1000 / TICK_HZ;

/** Mirrors fire() in shared: a held weapon always fires. */
function canFire(p: Player): boolean {
  return p.weapon !== null;
}

/** Everything that belongs to one match on screen; replaced by the next `start`. */
interface Match {
  me: PlayerId;
  prediction: Prediction;
  correction: Correction;
  interpolation: Interpolation;
  opponent: StartMessage['opponent'];
  board: BoardController;
  /** `end` received: the scene freezes, inputs stop */
  ended: boolean;
}

function endDetail(msg: EndMessage, me: PlayerId): string | null {
  if (msg.reason === 'forfeit') return msg.winner === me ? 'Der Gegner hat aufgegeben oder die Verbindung verloren.' : 'Aufgegeben.';
  return resultDetail(msg.snapshot.outcome, msg.winner, me);
}

/**
 * Plays against another player (or the server's bot) over the network. The same fixed-step loop as
 * the local mode drives the prediction of the own figure; the server's snapshots pull it straight
 * (netcode.ts) and feed the delayed, interpolated view of everything else. The board follows the
 * prediction (composeScene): an own move shows at once, rides on the next input like a shot, and
 * snaps back when the server's snapshot does not carry it. The session (session.ts) owns the socket;
 * this file only wires screen, input and simulation to it.
 */
export function startOnlineMode(opts: OnlineModeOptions): ModeHandle {
  const settings = loadSettings();
  const audio = new ChessAudio(settings.chessSounds !== false);
  const arenaAudio = new ArenaAudio(settings.arenaSounds !== false);
  const soundEvents = new ChessSoundEvents();
  const renderer = createRenderer(opts.canvas);
  const hud = createHud(opts.hud, { settings, onChange: () => { saveSettings(settings); audio.setEnabled(settings.chessSounds !== false); arenaAudio.setEnabled(settings.arenaSounds !== false); } });
  hud.setFoeLabel('Gegner');
  const input = attachInput(opts.canvas, settings);
  const { model } = input;
  const boardHost = { setBoard: input.setBoard, showPromotion: hud.showPromotion, hidePromotion: hud.hidePromotion, playSelection: () => audio.play('select') };

  // the arena behind the lobby until the first match arrives
  const preview: State = createInitialState(ARENA, 1);
  let match: Match | null = null;
  let scene: State = preview;
  let rttMs: number | null = null;
  let statusAt = performance.now();
  let lastSnapshotAt = 0;
  let lastPingAt = 0;
  let note: string | null = null;
  let resultShown = false;
  /** the board of the previous match keeps its last picture until the next `start` */
  let boardScene: ReturnType<BoardController['sync']> = null;

  let camera: Camera = fitWorld({ width: 1, height: 1 }, 1, 1);
  let dpr = 1;
  let topInset: number = CAMERA_STYLE.hudHeight;
  let viewport = { width: 1, height: 1 };
  function layout(): void {
    viewport = { width: window.innerWidth, height: window.innerHeight };
    dpr = Math.min(window.devicePixelRatio || 1, CAMERA_STYLE.maxDpr);
    topInset = opts.hud.querySelector('.hud-bar')?.getBoundingClientRect().bottom ?? CAMERA_STYLE.hudHeight;
    camera = followWorld(viewport, scene.map.width * TILE, scene.map.height * TILE, scene.players[match?.me ?? 0].pos, topInset, settings.overview);
    renderer.resize(viewport, dpr);
  }
  window.addEventListener('resize', layout);
  layout();
  const toWorld = (p: { x: number; y: number }) => screenToWorld(camera, p);

  const lobby = createLobby(opts.hud, {
    onPlay: () => {
      note = null;
      session.play();
    },
    onCancel: () => session.cancel(),
    onResume: () => session.connect(),
    onReload: () => window.location.reload(),
    onLocal: () => {
      const url = new URL(window.location.href);
      url.searchParams.set('mode', 'local');
      window.location.assign(url);
    },
  });

  function foeLabel(m: Match, latest: State): string {
    if (m.opponent === 'bot') return 'Bot';
    return latest.players[opponentOf(m.me)].connected ? 'Gegner' : 'Gegner · getrennt';
  }

  const session = new Session({
    url: opts.url,
    connect: opts.connect,
    tokens: opts.tokens,
    events: {
      onStatus: () => {
        statusAt = performance.now();
        if (!session.isPlaying()) { match?.board.detach(); audio.stop(); arenaAudio.stop(); }
      },
      onStart: (msg) => {
        audio.stop();
        soundEvents.reset(msg.state);
        arenaAudio.reset(msg.state);
        match?.board.detach();
        match = {
          me: msg.playerId,
          prediction: new Prediction(msg.state, msg.playerId),
          correction: new Correction(),
          interpolation: new Interpolation(msg.state, opponentOf(msg.playerId)),
          opponent: msg.opponent,
          board: new BoardController(msg.playerId, boardHost, BOARD_STYLE),
          ended: false,
        };
        accumulator = 0;
        resultShown = false;
        lastSnapshotAt = performance.now();
        hud.hideResult();
        hud.setFoeLabel(foeLabel(match, msg.state));
      },
      onSnapshot: (msg) => {
        if (!match || match.ended) return;
        if (msg.snapshot.phase !== match.interpolation.latest().phase) audio.stop();
        match.interpolation.push(msg.snapshot);
        arenaAudio.update(match.interpolation.latest(), match.me);
        const sound = soundEvents.update(match.interpolation.latest());
        if (sound) audio.play(sound);
        match.correction.add(match.prediction.reconcile(msg.snapshot, msg.lastProcessedSeq));
        lastSnapshotAt = performance.now();
      },
      onEnd: (msg) => {
        if (!match) return;
        match.ended = true;
        match.interpolation.push(msg.snapshot);
        arenaAudio.update(match.interpolation.latest(), match.me);
        const sound = soundEvents.update(match.interpolation.latest());
        if (sound) audio.play(sound);
        match.prediction.reconcile(msg.snapshot, Number.MAX_SAFE_INTEGER);
        if (!resultShown) {
          resultShown = true;
          const me = match.me;
          hud.showResult(
            msg.winner,
            me,
            () => {
              resultShown = false;
              hud.hideResult();
            },
            endDetail(msg, me),
          );
        }
      },
      onMatchLost: () => {
        match?.board.detach();
        arenaAudio.reset();
        match = null;
        boardScene = null;
        scene = preview;
        resultShown = false;
        hud.hideResult();
        note = 'Die Verbindung war zu lange weg – das Match ist vorbei.';
      },
      onPong: (ms) => {
        rttMs = Math.round(ms);
      },
    },
  });
  session.connect();

  function surrender(): void {
    if (!match || match.ended || !session.isPlaying()) return;
    session.giveUp();
  }

  let accumulator = 0;
  let last = performance.now();
  function frame(now: number): void {
    const dt = Math.min(now - last, MAX_FRAME_MS);
    last = now;

    if (match && !match.ended && session.isPlaying()) {
      const m = match;
      accumulator += dt;
      while (accumulator >= TICK_MS) {
        const mine = m.prediction.state().players[m.me];
        const input = model.take(m.prediction.nextSeq(), now, mine.pos, toWorld);
        const move = m.board.take(m.prediction.state());
        if (move) input.chessMove = move;
        if (input.fireAt && !canFire(mine) && m.prediction.state().phase === 'shooter') hud.flashSlot();
        session.sendInput(input);
        m.prediction.apply(input);
        accumulator -= TICK_MS;
      }
    } else {
      accumulator = 0;
    }

    let hudState = scene;
    if (match) {
      match.interpolation.advance(dt);
      match.correction.advance(dt);
      scene = composeScene(match.prediction, match.correction, match.interpolation.sample());
      const latest = match.interpolation.latest();
      hudState = composeHudState(match.prediction, latest);
      hud.setFoeLabel(foeLabel(match, latest));
    }

    const touch = model.touch();
    const me: PlayerId = match ? match.me : 0;
    camera = followWorld(viewport, scene.map.width * TILE, scene.map.height * TILE, scene.players[me].pos, topInset, settings.overview);
    if (match) boardScene = match.board.sync(scene, viewport, topInset, !match.ended && session.isPlaying());
    opts.canvas.classList.toggle('board', boardScene !== null);
    renderer.draw(scene, { confirmedHits: match?.interpolation.latest() ?? scene, me, camera, touch: touch.touched ? touch : null, cursor: model.cursor(), aimLine: settings.aimLine, dpr, viewport, topInset, board: boardScene });
    hud.setSurrender(surrender, match !== null && !match.ended && session.isPlaying());
    hud.update(hudState, me, boardScene ? { layout: boardScene.layout, check: boardScene.position.check } : null);
    hud.setNet(rttMs === null ? '' : `${rttMs} ms`);

    const status: SessionStatus = session.status();
    lobby.render(
      lobbyView(status, {
        inMatch: match !== null && !match.ended,
        resultVisible: resultShown,
        sinceStatusMs: now - statusAt,
        sinceSnapshotMs: match && !match.ended && status.kind === 'playing' ? now - lastSnapshotAt : null,
        stallWarningMs: STALL_WARNING_MS,
        note,
      }),
    );

    if (now - lastPingAt >= PING_INTERVAL_MS) {
      lastPingAt = now;
      session.ping();
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    last = performance.now();
    session.wake();
  });
  window.addEventListener('online', () => session.wake());

  return {
    state: () => scene,
    touch: () => model.touch(),
    board: () => match?.board.layout ?? null,
    net: () => ({
      status: session.status(),
      me: match ? match.me : 0,
      rttMs,
      pending: match ? match.prediction.pendingCount() : 0,
      correction: match ? match.correction.current() : { x: 0, y: 0 },
      latest: match ? match.interpolation.latest() : null,
      dropConnection: () => session.dropConnection(),
    }),
  };
}
